import { forwardRef, useEffect, useId, useState, type InputHTMLAttributes, type ReactNode, type SelectHTMLAttributes, type TextareaHTMLAttributes } from 'react';
import { AlertCircle, ChevronDown, Eye, EyeOff, Search, X } from 'lucide-react';
import { m } from 'motion/react';
import { formatMoneyPlain } from '@zuri/shared';
import { cx } from '../../lib/format';

interface FieldProps {
  label?: ReactNode;
  hint?: ReactNode;
  error?: string | null;
  required?: boolean;
  className?: string;
  children: (props: { id: string; 'aria-invalid'?: boolean; 'aria-describedby'?: string }) => ReactNode;
  trailing?: ReactNode;
}

/** Rótulo + controle + ajuda/erro com associação acessível. */
export function Field({ label, hint, error, required, className, children, trailing }: FieldProps) {
  const id = useId();
  const describedBy = error ? `${id}-err` : hint ? `${id}-hint` : undefined;
  return (
    <div className={cx('flex min-w-0 flex-col gap-1.5', className)}>
      {label && (
        <div className="flex items-baseline justify-between gap-2">
          <label htmlFor={id} className="text-[13px] font-[560] text-ink-2">
            {label}
            {required && <span className="ml-0.5 text-danger" aria-hidden>*</span>}
          </label>
          {trailing}
        </div>
      )}
      {children({ id, 'aria-invalid': error ? true : undefined, 'aria-describedby': describedBy })}
      {error ? (
        <p id={`${id}-err`} className="flex items-start gap-1.5 text-[12.5px] font-medium text-danger" role="alert">
          <AlertCircle className="mt-[1px] size-3.5 shrink-0" aria-hidden />
          {error}
        </p>
      ) : hint ? (
        <p id={`${id}-hint`} className="text-[12px] text-ink-3">
          {hint}
        </p>
      ) : null}
    </div>
  );
}

export const TextInput = forwardRef<HTMLInputElement, InputHTMLAttributes<HTMLInputElement>>(function TextInput({ className, ...rest }, ref) {
  return <input ref={ref} className={cx('field', className)} {...rest} />;
});

export const TextArea = forwardRef<HTMLTextAreaElement, TextareaHTMLAttributes<HTMLTextAreaElement>>(function TextArea({ className, ...rest }, ref) {
  return <textarea ref={ref} rows={3} className={cx('field resize-y py-2.5', className)} {...rest} />;
});

export const Select = forwardRef<HTMLSelectElement, SelectHTMLAttributes<HTMLSelectElement>>(function Select({ className, children, ...rest }, ref) {
  return (
    <div className="relative">
      <select ref={ref} className={cx('field appearance-none pr-9', className)} {...rest}>
        {children}
      </select>
      <ChevronDown className="pointer-events-none absolute right-3 top-1/2 size-4 -translate-y-1/2 text-ink-3" aria-hidden />
    </div>
  );
});

interface MoneyInputProps extends Omit<InputHTMLAttributes<HTMLInputElement>, 'value' | 'onChange'> {
  value: number | null;
  onChange: (cents: number | null) => void;
  allowEmpty?: boolean;
}

/**
 * Valor em reais com máscara pt-BR: os dígitos preenchem os centavos ("12345" → 123,45).
 * Campo vazio = valor não informado (null), nunca zero.
 */
export const MoneyInput = forwardRef<HTMLInputElement, MoneyInputProps>(function MoneyInput({ value, onChange, className, allowEmpty = true, ...rest }, ref) {
  const [text, setText] = useState(value === null ? '' : formatMoneyPlain(value));
  useEffect(() => {
    setText(value === null ? '' : formatMoneyPlain(value));
  }, [value]);
  return (
    <div className="relative">
      <span className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-[13px] text-ink-3">R$</span>
      <input
        ref={ref}
        inputMode="numeric"
        autoComplete="off"
        className={cx('field num pl-9 text-right', className)}
        value={text}
        placeholder={allowEmpty ? 'Não informado' : '0,00'}
        onChange={(e) => {
          const digits = e.target.value.replace(/\D/g, '').replace(/^0+(?=\d)/, '').slice(0, 15);
          if (!digits) {
            setText('');
            onChange(allowEmpty ? null : 0);
            return;
          }
          const cents = Number(digits);
          setText(formatMoneyPlain(cents));
          onChange(cents);
        }}
        {...rest}
      />
    </div>
  );
});

export const DateInput = forwardRef<HTMLInputElement, InputHTMLAttributes<HTMLInputElement>>(function DateInput({ className, ...rest }, ref) {
  return <input ref={ref} type="date" className={cx('field num', className)} {...rest} />;
});

export function Switch({ checked, onChange, label, description, disabled }: { checked: boolean; onChange: (v: boolean) => void; label: ReactNode; description?: ReactNode; disabled?: boolean }) {
  const id = useId();
  return (
    <div className="flex items-start justify-between gap-4 py-1">
      <label htmlFor={id} className="min-w-0 cursor-pointer">
        <div className="text-[14px] font-medium text-ink">{label}</div>
        {description && <div className="mt-0.5 text-[12.5px] text-ink-3">{description}</div>}
      </label>
      <button
        id={id}
        type="button"
        role="switch"
        aria-checked={checked}
        disabled={disabled}
        onClick={() => onChange(!checked)}
        className={cx(
          'relative mt-0.5 h-[26px] w-[44px] shrink-0 rounded-full transition-colors duration-200 disabled:opacity-50',
          checked ? 'bg-primary' : 'bg-[color-mix(in_srgb,var(--text)_16%,transparent)]',
        )}
      >
        <span aria-hidden className={cx('absolute left-0 top-[3px] size-5 rounded-full bg-white shadow-[0_2px_4px_rgba(0,0,0,0.2)] transition-transform duration-200', checked ? 'translate-x-[21px]' : 'translate-x-[3px]')} />
      </button>
    </div>
  );
}

export function Checkbox({ checked, onChange, label, disabled }: { checked: boolean; onChange: (v: boolean) => void; label: ReactNode; disabled?: boolean }) {
  return (
    <label className={cx('inline-flex cursor-pointer items-center gap-2.5 text-[14px]', disabled && 'cursor-not-allowed opacity-50')}>
      <input type="checkbox" className="size-[18px] cursor-pointer rounded accent-[var(--primary)]" checked={checked} disabled={disabled} onChange={(e) => onChange(e.target.checked)} />
      <span>{label}</span>
    </label>
  );
}

/**
 * Controle segmentado (estilo iOS): o indicador desliza entre as opções (mola discreta) e as
 * setas do teclado mudam a seleção.
 */
export function Segmented<T extends string>({ value, onChange, options, size = 'md', className, ariaLabel }: { value: T; onChange: (v: T) => void; options: Array<{ value: T; label: ReactNode; count?: number }>; size?: 'sm' | 'md'; className?: string; ariaLabel?: string }) {
  const id = useId();
  const onKey = (e: React.KeyboardEvent) => {
    if (e.key !== 'ArrowRight' && e.key !== 'ArrowLeft') return;
    const i = options.findIndex((o) => o.value === value);
    const next = options[(i + (e.key === 'ArrowRight' ? 1 : options.length - 1)) % options.length];
    onChange(next.value);
    e.preventDefault();
  };
  return (
    <div role="tablist" aria-label={ariaLabel} onKeyDown={onKey} className={cx('relative inline-flex max-w-full overflow-x-auto rounded-[12px] bg-sunken p-[3px] shadow-[inset_0_0_0_1px_var(--separator)] [scrollbar-width:none] [&::-webkit-scrollbar]:hidden', className)}>
      {options.map((o) => {
        const active = o.value === value;
        return (
          <button
            key={o.value}
            type="button"
            role="tab"
            aria-selected={active}
            tabIndex={active ? 0 : -1}
            onClick={() => onChange(o.value)}
            className={cx(
              'relative inline-flex shrink-0 items-center gap-1.5 whitespace-nowrap rounded-[9px] font-[560] transition-colors duration-150',
              size === 'sm' ? 'h-7 px-2.5 text-[12.5px] coarse:h-9' : 'h-8 px-3.5 text-[13px] coarse:h-10',
              active ? 'text-ink' : 'text-ink-2 hover:text-ink',
            )}
          >
            {active && <m.span layoutId={`seg-${id}`} className="absolute inset-0 rounded-[9px] bg-surface shadow-[0_1px_2px_rgba(0,0,0,0.08),0_0_0_0.5px_rgba(0,0,0,0.06)]" transition={{ type: 'spring', stiffness: 520, damping: 40, mass: 0.9 }} aria-hidden />}
            <span className="relative inline-flex items-center gap-1.5 [&_svg]:size-3.5">{o.label}</span>
            {o.count !== undefined && o.count > 0 && <span className={cx('num relative rounded-full px-1.5 text-[11px] font-semibold', active ? 'bg-primary-soft text-primary-text' : 'bg-active text-ink-2')}>{o.count}</span>}
          </button>
        );
      })}
    </div>
  );
}

/** Senha com mostrar/ocultar acessível; compatível com gerenciadores de senha (autocomplete). */
export const PasswordInput = forwardRef<HTMLInputElement, InputHTMLAttributes<HTMLInputElement>>(function PasswordInput({ className, ...rest }, ref) {
  const [visible, setVisible] = useState(false);
  return (
    <div className="relative">
      <input ref={ref} type={visible ? 'text' : 'password'} className={cx('field pr-12', className)} autoCapitalize="none" autoCorrect="off" spellCheck={false} {...rest} />
      <button
        type="button"
        onClick={() => setVisible((v) => !v)}
        aria-label={visible ? 'Ocultar senha' : 'Mostrar senha'}
        aria-pressed={visible}
        className="absolute right-1 top-1/2 grid size-9 -translate-y-1/2 place-items-center rounded-[10px] text-ink-3 transition-colors hover:bg-hover hover:text-ink coarse:size-10"
      >
        {visible ? <EyeOff className="size-[18px]" aria-hidden /> : <Eye className="size-[18px]" aria-hidden />}
      </button>
    </div>
  );
});

export function SearchInput({ value, onChange, placeholder = 'Buscar', className }: { value: string; onChange: (v: string) => void; placeholder?: string; className?: string }) {
  return (
    <div className={cx('relative', className)}>
      <Search className="pointer-events-none absolute left-3 top-1/2 size-4 -translate-y-1/2 text-ink-3" aria-hidden />
      <input type="search" value={value} onChange={(e) => onChange(e.target.value)} placeholder={placeholder} aria-label={placeholder} className="field h-10 min-h-10 rounded-[12px] border-transparent bg-sunken pl-9 pr-9 shadow-[inset_0_0_0_1px_var(--separator)] hover:border-transparent coarse:h-11 [&::-webkit-search-cancel-button]:hidden" />
      {value && (
        <button type="button" aria-label="Limpar busca" onClick={() => onChange('')} className="absolute right-1.5 top-1/2 grid size-8 -translate-y-1/2 place-items-center rounded-full text-ink-3 hover:bg-hover hover:text-ink">
          <X className="size-3.5" />
        </button>
      )}
    </div>
  );
}
